/**
 * Account Updates
 *
 * Pure functions for updating account fields and deleting accounts.
 * Part of the accounts domain (split from accountHelpers.ts).
 */

import { AccountsState, RelationshipGraph, OnboardingStepState, Classroom } from '../types';
import { error as logError } from '../../utils/logger';

/**
 * Update account display name
 */
export function updateAccountName(accountsState: AccountsState, accountId: string, newName: string): AccountsState {
  const account = accountsState.accountsById[accountId];
  if (!account) {
    logError(`Account not found: ${accountId}`);
    return accountsState;
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [accountId]: {
        ...account,
        displayName: newName,
        lastActive: Date.now()
      }
    }
  };
}

/**
 * Validate an email update against current state.
 *
 * Returns `null` if the change is allowed, or a Dutch error string otherwise.
 *
 * Checks:
 *   - format (RFC-light regex, matching `validateRegistrationInput`)
 *   - uniqueness (no other account with the same email)
 *   - target account exists
 *
 * Consumers should call this BEFORE invoking `updateAccountEmail` so they can
 * surface a useful error. `updateAccountEmail` ALSO calls this defensively
 * and silently refuses invalid updates as a safety net.
 */
export function validateEmailUpdate(accountsState: AccountsState, accountId: string, email: string): string | null {
  const account = accountsState.accountsById[accountId];
  if (!account) return 'Account niet gevonden.';
  const trimmed = email.trim().toLowerCase();
  if (!trimmed) return 'Vul een e-mailadres in.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return 'Vul een geldig e-mailadres in.';
  }

  // Uniqueness check: any OTHER account with this email is a conflict.
  for (const other of Object.values(accountsState.accountsById)) {
    if (other.id === accountId) continue;
    if (other.email && other.email.toLowerCase() === trimmed) {
      return 'Dit e-mailadres is al in gebruik door een ander account.';
    }
  }
  return null;
}

/**
 * Update account email.
 *
 * SAFETY: if `validateEmailUpdate` rejects the update (bad format, duplicate,
 * or unknown account), this function returns the unchanged state instead of
 * persisting corrupt data. UI consumers should call `validateEmailUpdate`
 * first to surface the error.
 */
export function updateAccountEmail(accountsState: AccountsState, accountId: string, email: string): AccountsState {
  const account = accountsState.accountsById[accountId];
  if (!account) {
    return accountsState;
  }
  const error = validateEmailUpdate(accountsState, accountId, email);
  if (error !== null) {
    logError(`updateAccountEmail rejected: ${error}`);
    return accountsState;
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [accountId]: {
        ...account,
        email: email.trim().toLowerCase(),
        lastActive: Date.now()
      }
    }
  };
}

/**
 * Update account password hash
 */
export function updateAccountPassword(accountsState: AccountsState, accountId: string, newPasswordHash: string): AccountsState {
  const account = accountsState.accountsById[accountId];
  if (!account) {
    logError(`Account not found: ${accountId}`);
    return accountsState;
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [accountId]: {
        ...account,
        passwordHash: newPasswordHash,
        lastActive: Date.now()
      }
    }
  };
}

/**
 * Update teacher details
 */
export function updateTeacherDetails(accountsState: AccountsState, teacherId: string, updates: {
  schoolName?: string;
}): AccountsState {
  const account = accountsState.accountsById[teacherId];
  if (!account || account.role !== 'teacher') {
    return accountsState;
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [teacherId]: {
        ...account,
        ...updates,
        lastActive: Date.now()
      }
    }
  };
}

/**
 * Update learner link code
 */
export function updateLearnerLinkCode(accountsState: AccountsState, learnerId: string, linkCode: string): AccountsState {
  const account = accountsState.accountsById[learnerId];
  if (!account || account.role !== 'learner') {
    return accountsState;
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [learnerId]: {
        ...account,
        linkCode,
        lastActive: Date.now()
      }
    }
  };
}

/**
 * Update learner grade
 */
export function updateLearnerGrade(accountsState: AccountsState, learnerId: string, grade: number): AccountsState {
  const account = accountsState.accountsById[learnerId];
  if (!account || account.role !== 'learner') {
    return accountsState;
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [learnerId]: {
        ...account,
        grade,
        lastActive: Date.now()
      }
    }
  };
}

/**
 * Update learner-specific details (grade, username).
 * Pure function — returns updated AccountsState.
 */
export function updateLearnerDetails(accountsState: AccountsState, learnerId: string, updates: {
  grade?: number;
  username?: string;
}): AccountsState {
  const account = accountsState.accountsById[learnerId];
  if (!account || account.role !== 'learner') {
    return accountsState;
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [learnerId]: {
        ...account,
        ...updates,
        lastActive: Date.now()
      }
    }
  };
}

/**
 * Touch lastActive timestamp on an account (for session keep-alive).
 * Pure function — returns updated AccountsState.
 */
export function touchLastActive(state: AccountsState, accountId: string): AccountsState {
  const account = state.accountsById[accountId];
  if (!account) return state;
  const now = Date.now();
  // Debounce updates to prevent excessive re-renders (only update if > 1 min ago)
  if (now - account.lastActive < 60000) {
    return state;
  }
  return {
    ...state,
    accountsById: {
      ...state.accountsById,
      [accountId]: {
        ...account,
        lastActive: now
      }
    }
  };
}

/**
 * Delete an account from the accounts state, cleaning up all relationships.
 * Pure function — returns updated AccountsState.
 * Returns unchanged state if trying to delete the last admin.
 *
 * ⚠️ IMPORTANT: This function only operates on AccountsState (accounts + relationships).
 * It does NOT clean up `learnerData` (progress, mastery, XP, etc.) for deleted learners.
 *
 * **Do NOT call this directly** — use `accountActions.deleteAccount()` from
 * `useAccountActions` instead, which wraps this function and also removes
 * the learner's data from `learnerData[accountId]`.
 *
 * ACTIVE-ACCOUNT POLICY: if the deleted account was the active one,
 * `activeAccountId` is cleared to `''` (forcing the AuthGuard to show the
 * account picker / login). We previously auto-fell-back to the first
 * remaining account, but that was non-deterministic (insertion order) and
 * could drop a user into an unrelated profile without warning.
 *
 * @see useAccountActions.deleteAccount — safe wrapper that handles full cleanup
 */
export function deleteAccountFromState(accountsState: AccountsState, accountId: string): AccountsState {
  const account = accountsState.accountsById[accountId];
  if (!account) return accountsState;

  // Don't allow deleting the last admin
  if (account.role === 'admin') {
    const adminCount = Object.values(accountsState.accountsById).filter(a => a.role === 'admin').length;
    if (adminCount <= 1) return accountsState;
  }
  const newAccountsById = {
    ...accountsState.accountsById
  };
  delete newAccountsById[accountId];
  let newClassrooms = {
    ...accountsState.classrooms
  };

  // Clean up relationships
  const newRelationships: RelationshipGraph = {
    parentToLearners: {
      ...accountsState.relationships.parentToLearners
    },
    learnerToParents: {
      ...accountsState.relationships.learnerToParents
    },
    teacherToLearners: {
      ...accountsState.relationships.teacherToLearners
    },
    learnerToTeachers: {
      ...accountsState.relationships.learnerToTeachers
    },
    learnerToClassrooms: {
      ...(accountsState.relationships.learnerToClassrooms || {})
    },
    archivedLearnerLinks: {
      ...(accountsState.relationships.archivedLearnerLinks || {})
    }
  };
  if (account.role === 'parent') {
    delete newRelationships.parentToLearners[accountId];
    Object.keys(newRelationships.learnerToParents).forEach(lId => {
      newRelationships.learnerToParents[lId] = newRelationships.learnerToParents[lId].filter(id => id !== accountId);
    });
  } else if (account.role === 'teacher') {
    // Remove teacher's forward mapping
    const linkedLearners = newRelationships.teacherToLearners[accountId] || [];
    delete newRelationships.teacherToLearners[accountId];
    // Remove teacher from all linked learners' reverse mapping (O(k) where k = linked learners)
    linkedLearners.forEach(lId => {
      if (newRelationships.learnerToTeachers[lId]) {
        newRelationships.learnerToTeachers[lId] = newRelationships.learnerToTeachers[lId].filter(id => id !== accountId);
      }
    });

    // Clean up classrooms owned by this teacher and co-teacher memberships
    for (const [classroomId, classroom] of Object.entries(newClassrooms)) {
      if (classroom.teacherId === accountId) {
        // Delete classroom
        delete newClassrooms[classroomId];
        // Clean up learnerToClassrooms reverse index for all learners in this classroom
        for (const learnerId of classroom.learnerIds) {
          if (newRelationships.learnerToClassrooms[learnerId]) {
            newRelationships.learnerToClassrooms[learnerId] = newRelationships.learnerToClassrooms[learnerId].filter(id => id !== classroomId);
          }
        }
      } else if (classroom.coTeacherIds && classroom.coTeacherIds.includes(accountId)) {
        // Remove from coTeacherIds
        newClassrooms[classroomId] = {
          ...classroom,
          coTeacherIds: classroom.coTeacherIds.filter(id => id !== accountId)
        };
      }
    }
  } else if (account.role === 'learner') {
    delete newRelationships.learnerToParents[accountId];
    delete newRelationships.learnerToTeachers[accountId];
    Object.keys(newRelationships.parentToLearners).forEach(pId => {
      newRelationships.parentToLearners[pId] = newRelationships.parentToLearners[pId].filter(id => id !== accountId);
    });
    // Use learnerToTeachers for efficient cleanup instead of iterating all teachers
    const linkedTeachers = accountsState.relationships.learnerToTeachers?.[accountId] || [];
    linkedTeachers.forEach(tId => {
      if (newRelationships.teacherToLearners[tId]) {
        newRelationships.teacherToLearners[tId] = newRelationships.teacherToLearners[tId].filter(id => id !== accountId);
      }
    });

    // Clean up learnerToClassrooms
    delete newRelationships.learnerToClassrooms[accountId];
    // Clean up classroom learnerIds
    const linkedClassrooms = accountsState.relationships.learnerToClassrooms?.[accountId] || [];
    linkedClassrooms.forEach(cId => {
      if (newClassrooms[cId]) {
        newClassrooms[cId] = {
          ...newClassrooms[cId],
          learnerIds: newClassrooms[cId].learnerIds.filter(id => id !== accountId)
        };
      }
    });
  }
  return {
    ...accountsState,
    accountsById: newAccountsById,
    classrooms: newClassrooms,
    relationships: newRelationships,
    // F-ACC-EDGE-R2-DELETED-ACTIVE-RANDOMLY-RESTORED:
    // If the active account was deleted, clear the active id rather than
    // auto-selecting an arbitrary remaining account. The AuthGuard will
    // surface the account picker / login screen instead of silently
    // dropping the user into an unrelated profile.
    activeAccountId: accountsState.activeAccountId === accountId ? '' : accountsState.activeAccountId
  };
}

/**
 * Mark an account's onboarding as completed.
 * Sets hasCompletedOnboarding = true on the specified account.
 * Also saves the granular step state if provided.
 */
export function completeOnboarding(accountsState: AccountsState, accountId: string, stepState?: OnboardingStepState): AccountsState {
  const account = accountsState.accountsById[accountId];
  if (!account) return accountsState;
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [accountId]: {
        ...account,
        hasCompletedOnboarding: true,
        ...(stepState ? {
          onboardingSteps: stepState
        } : {})
      }
    }
  };
}
export interface AccountDeletionDecision {
  classroomId: string;
  action: 'transfer' | 'delete' | 'orphan';
  newTeacherId?: string;
  keepAsCoTeacher?: boolean;
}
export interface AccountDeletionPreparation {
  ownedClassrooms: Array<{
    classroom: Classroom;
    studentCount: number;
    coTeachers: Array<{
      id: string;
      name: string;
    }>;
    schoolColleagues: Array<{
      id: string;
      name: string;
    }>;
  }>;
}

/**
 * Prepare account deletion by gathering decisions needed for owned classrooms.
 */
export function prepareAccountDeletion(state: AccountsState, teacherId: string): AccountDeletionPreparation {
  const teacher = state.accountsById[teacherId];
  const schoolName = teacher?.role === 'teacher' ? teacher.schoolName?.trim().toLowerCase() : '';
  const ownedClassrooms = Object.values(state.classrooms).filter(c => c.teacherId === teacherId);
  const schoolColleagues = Object.values(state.accountsById).filter(a => a.role === 'teacher' && a.id !== teacherId && a.schoolName?.trim().toLowerCase() === schoolName && schoolName !== '').map(a => ({
    id: a.id,
    name: a.displayName
  }));
  return {
    ownedClassrooms: ownedClassrooms.map(c => {
      const coTeachers = (c.coTeacherIds || []).map(id => {
        const acc = state.accountsById[id];
        return {
          id,
          name: acc?.displayName || 'Onbekend'
        };
      });
      return {
        classroom: c,
        studentCount: c.learnerIds.length,
        coTeachers,
        schoolColleagues: schoolColleagues.filter(sc => !c.coTeacherIds?.includes(sc.id))
      };
    })
  };
}

/**
 * Execute account deletion by applying decisions and then deleting the account.
 */
export function executeAccountDeletion(state: AccountsState, teacherId: string, decisions: AccountDeletionDecision[]): AccountsState {
  // Lazy import to avoid cycle if needed, but we can just import at top or require
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const {
    transferClassroomOwnership,
    removeClassroom
  } = require('./classrooms') as typeof import('./classrooms');
  let nextState = state;
  for (const decision of decisions) {
    if (decision.action === 'transfer' && decision.newTeacherId) {
      nextState = transferClassroomOwnership(nextState,
      // `null` = systeemoperatie. Dit draait tijdens het opheffen van een
      // leerkrachtaccount, dus de eigenaar is juist degene die verdwijnt;
      // een eigenaarscontrole zou hier altijd falen en de klassen verweesd
      // achterlaten.
      null, decision.classroomId, decision.newTeacherId, decision.keepAsCoTeacher);
    } else if (decision.action === 'delete') {
      nextState = removeClassroom(nextState, teacherId, decision.classroomId);
    } else if (decision.action === 'orphan') {
      const classroom = nextState.classrooms[decision.classroomId];
      if (classroom) {
        nextState = {
          ...nextState,
          classrooms: {
            ...nextState.classrooms,
            [decision.classroomId]: {
              ...classroom,
              // Use named sentinel prefix instead of magic-string concatenation.
              // Downstream lookups MUST guard with isOrphanedTeacherId().
              teacherId: ORPHAN_TEACHER_PREFIX + teacherId
            }
          }
        };
      }
    }
  }
  return deleteAccountFromState(nextState, teacherId);
}

/**
 * Sentinel prefix marking a classroom whose owner has been deleted via the
 * "orphan" decision in `executeAccountDeletion`. Such classrooms' `teacherId`
 * does NOT reference a real account — downstream consumers MUST guard with
 * `isOrphanedTeacherId(classroom.teacherId)` before doing account lookups.
 *
 * Replaces the previous magic-string `'ORPHAN_' + teacherId` convention so
 * the contract is discoverable via type/IDE search.
 */
export const ORPHAN_TEACHER_PREFIX = 'ORPHAN_';

/** True if a classroom's teacherId is a sentinel from an orphan-deletion. */
export function isOrphanedTeacherId(teacherId: string): boolean {
  return teacherId.startsWith(ORPHAN_TEACHER_PREFIX);
}